import type { ProductAnalytic } from '@prisma/client'
import { Link, useLocation } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Media } from '~/components/Media/Media'
import { MediaCard } from '~/components/media-card'
import { ProductAnalytics } from '~/components/product/product-analytics'
import { getProductDisplayPrice } from '~/helpers/product'
import { makeShopOwnPath } from '~/helpers/routes'
import { getEntireStoreDiscount } from '~/helpers/shop'
import { useOptionalSeller } from '~/hooks/data/use-seller'
import { useShop } from '~/hooks/data/use-shop'
import type { ProductFull } from '~/models/product.server'
import { Badge } from '~/ui'
import { centsToEurosFormattedPrice } from '~/utils/number'
import { getProductCoverMedia } from '~/utils/product'

interface CollectionProductCardProps {
  product: SerializeFrom<ProductFull>
  analytics?: SerializeFrom<ProductAnalytic>
  active?: boolean
  aspectRatio?: 'square' | 'landscape' | 'portrait'
  onRemove?: () => void
}

const STATUS_TRANSLATION_KEYS = {
  draft: 'noun.draft',
  live: 'noun.live',
  presale: 'noun.pre_sale',
  preview: 'noun.coming_soon',
  archived: 'noun.archived',
} as const

export const CollectionProductCard = ({
  product,
  analytics,
  active,
  aspectRatio,
  onRemove,
}: CollectionProductCardProps) => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const shop = useShop()
  const seller = useOptionalSeller()
  const shopDiscount = useMemo(() => getEntireStoreDiscount(shop), [shop])

  const price = useMemo(
    () => ({
      current: getProductDisplayPrice(product, shopDiscount || null) || 0,
      previous: shopDiscount ? getProductDisplayPrice(product, null) : null,
    }),
    [shopDiscount, product]
  )

  const media = useMemo(() => getProductCoverMedia(product), [product])

  const status = useMemo(() => {
    if (product.archivedAt !== null) return 'archived'
    if (product.isPresale) return 'presale'
    if (product.isPreview) return 'preview'
    if (!product.publishedAt && !product.isPreview && !product.isPresale)
      return 'draft'

    return 'live'
  }, [product])

  return (
    <MediaCard format={aspectRatio || product.mediaFormat || 'square'} asChild>
      <ConditionalProductLink
        className="ring-0!"
        link={
          !seller
            ? makeShopOwnPath(
                shop,
                `/products/${product.id}/${product.slug}?backTo=${pathname}`
              )
            : null
        }
      >
        {seller && onRemove && <MediaCard.CloseButton onClick={onRemove} />}

        <div className="relative">
          {active && <MediaCard.Ring />}
          <MediaCard.Cover>
            {media && (
              <>
                <Media
                  src={media.src}
                  kind={media.kind}
                  transformation={{
                    width: 800,
                    format: 'webp',
                    resizeMode: 'fill',
                  }}
                  sizes={{
                    sm: '100vw',
                    md: '50vw',
                    default: '400px',
                  }}
                />
                {analytics && (
                  <div>
                    <ProductAnalytics
                      analytics={analytics}
                      isAnalyticsEnabled
                    />
                  </div>
                )}
              </>
            )}
          </MediaCard.Cover>
        </div>
        <MediaCard.Content>
          <MediaCard.Title className="pr-px">
            <h3>{product.name}</h3>
            {seller && (status === 'draft' || status === 'archived') && (
              <Badge>
                <Badge.Status status="neutral" />
                {t(STATUS_TRANSLATION_KEYS[status])}
              </Badge>
            )}
            {(status === 'preview' || status === 'presale') && (
              <Badge>{t(STATUS_TRANSLATION_KEYS[status])}</Badge>
            )}
          </MediaCard.Title>
          <MediaCard.Description>
            <span>{centsToEurosFormattedPrice(price.current)}</span>
            {price.previous !== null && (
              <span className="ml-1.5 line-through opacity-64">
                {centsToEurosFormattedPrice(price.previous)}
              </span>
            )}
          </MediaCard.Description>
        </MediaCard.Content>
      </ConditionalProductLink>
    </MediaCard>
  )
}

interface ConditionalProductLinkProps {
  link?: string | null
  className?: string
  children: React.ReactNode
}

const ConditionalProductLink = ({
  link,
  children,
  className,
}: ConditionalProductLinkProps) => {
  if (!link) return <div className={className}>{children}</div>

  return (
    <Link to={link} draggable={false} className={className}>
      {children}
    </Link>
  )
}
